import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { HubFormCard } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import s from "../clinic.module.css";
import { Button, useToast } from "../../ui";

const REASON_OPTIONS = [
  { id: "unexcused", label: "Без предупреждения" },
  { id: "illness", label: "Болезнь" },
  { id: "family", label: "Семейные обстоятельства" },
  { id: "competition", label: "Соревнование" },
  { id: "custom", label: "Своя причина" },
];

/**
 * Списание занятия с абонемента при пропуске: вкл/выкл и список причин.
 */
export default function AbsenceChargeSettingsPanel({ variant = "app" }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [reasons, setReasons] = useState(["unexcused"]);

  const applyResponse = (res) => {
    setEnabled(Boolean(res.absence_charge_enabled));
    setReasons(
      Array.isArray(res.absence_charge_reasons) &&
        res.absence_charge_reasons.length
        ? res.absence_charge_reasons
        : ["unexcused"],
    );
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clinicAPI.getAbsenceChargeSettings();
      applyResponse(res);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось загрузить настройки");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleReason = (id) => {
    setReasons((list) =>
      list.includes(id) ? list.filter((x) => x !== id) : [...list, id],
    );
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (enabled && reasons.length === 0) {
      toast.error("Выберите хотя бы одну причину");
      return;
    }
    setSaving(true);
    try {
      const res = await clinicAPI.saveAbsenceChargeSettings({
        absence_charge_enabled: enabled,
        absence_charge_reasons: reasons,
      });
      applyResponse(res);
      toast.success("Сохранено");
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className={aa.journalHint || s.clientTabMuted}>Загрузка…</p>;
  }

  const body = (
    <form onSubmit={save} className={aa.absenceChargeForm}>
      <label className={s.settingsToggleRow}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <span>
          <strong>Списывать занятие при пропуске</strong>
          <span className={s.branchCheckAddr}>
            Если выключено, пропуск не уменьшает остаток абонемента.
          </span>
        </span>
      </label>

      {enabled ? (
        <div className={aa.absenceChargeReasonsBlock}>
          <div className={aa.absenceChargeReasonsHead}>
            <span className={aa.absenceChargeReasonsTitle}>Списывать за</span>
            <span className={aa.absenceChargeReasonsHint}>
              Остальные причины остаток не трогают
            </span>
          </div>
          <div
            className={aa.absenceChargeReasonGrid}
            role="group"
            aria-label="Причины списания"
          >
            {REASON_OPTIONS.map((opt) => {
              const on = reasons.includes(opt.id);
              return (
                <label
                  key={opt.id}
                  className={
                    on ? aa.absenceChargeReasonChipOn : aa.absenceChargeReasonChip
                  }
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleReason(opt.id)}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className={aa.sessionFeeSaveRow}>
        <Button type="submit" disabled={saving}>
          {saving ? "Сохранение…" : "Сохранить"}
        </Button>
      </div>
    </form>
  );

  if (variant === "desktop") {
    return (
      <section className={md.desktopSettingsNotifyCard}>
        <h3 className={md.desktopSettingsSubheading}>Пропуски и абонемент</h3>
        <p className={md.desktopGroupSettingsLead}>
          Можно списывать занятие только за выбранные причины пропуска — например
          без предупреждения да, по болезни нет.
        </p>
        {body}
      </section>
    );
  }

  return (
    <HubFormCard title="Пропуски и абонемент">
      <p className={s.clientTabMuted} style={{ marginTop: 0, marginBottom: 12 }}>
        Можно списывать занятие только за выбранные причины пропуска — например
        без предупреждения да, по болезни нет.
      </p>
      {body}
    </HubFormCard>
  );
}
